import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, 
         startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, 
         isSameDay, isToday, addDays, subDays,
         startOfDay, endOfDay, isAfter, isBefore, isEqual,
         startOfYear, endOfYear, startOfQuarter, endOfQuarter, subQuarters, subYears } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';
import { useTranslation } from '../../hooks/useTranslation';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { FlattenedKeys } from '../../i18n/types';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (range: DateRange) => void;
  initialRange?: DateRange;
  position?: 'top' | 'bottom';
}

type PresetType = 
  | 'currentMonth' 
  | 'previousMonth' 
  | 'currentQuarter' 
  | 'previousQuarter' 
  | 'currentYear' 
  | 'previousYear' 
  | 'last30Days' 
  | 'last90Days' 
  | 'yearToDate' 
  | 'custom';

interface Preset {
  type: PresetType;
  label: string;
  getRange: () => DateRange;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  isOpen,
  onClose,
  onApply,
  initialRange,
  position = 'bottom'
}) => {
  const { t } = useTranslation();
  const language = useInvoiceStore((state) => state.settings.language);
  const locale = language === 'nl' ? nl : enUS;
  
  const today = new Date();
  const [leftMonth, setLeftMonth] = useState(startOfMonth(today));
  const [rightMonth, setRightMonth] = useState(startOfMonth(addMonths(today, 1)));
  
  const [selectedRange, setSelectedRange] = useState<DateRange>(initialRange || {
    startDate: startOfMonth(today),
    endDate: endOfMonth(today)
  });
  
  const [activePreset, setActivePreset] = useState<PresetType>('currentMonth');
  const [isSelectingFirstDate, setIsSelectingFirstDate] = useState(true);
  
  const containerRef = useRef<HTMLDivElement>(null);
  
  useClickOutside(containerRef, () => {
    if (isOpen) onClose();
  });
  
  // Reset state when opening
  useEffect(() => {
    if (isOpen && initialRange) {
      setSelectedRange(initialRange);
      setLeftMonth(startOfMonth(initialRange.startDate));
      setRightMonth(startOfMonth(addMonths(initialRange.startDate, 1)));
      setActivePreset('custom');
      setIsSelectingFirstDate(true);
    }
  }, [isOpen, initialRange]);
  
  // Updated presets for invoice management
  const presets: Preset[] = [
    {
      type: 'currentMonth',
      label: t('period.presets.currentMonth' as FlattenedKeys),
      getRange: () => ({
        startDate: startOfMonth(today),
        endDate: endOfMonth(today)
      })
    },
    {
      type: 'previousMonth',
      label: t('period.presets.previousMonth' as FlattenedKeys),
      getRange: () => {
        const lastMonth = subMonths(today, 1);
        return {
          startDate: startOfMonth(lastMonth),
          endDate: endOfMonth(lastMonth)
        };
      }
    },
    {
      type: 'currentQuarter',
      label: t('period.presets.currentQuarter' as FlattenedKeys),
      getRange: () => ({
        startDate: startOfQuarter(today),
        endDate: endOfQuarter(today)
      })
    },
    {
      type: 'previousQuarter',
      label: t('period.presets.previousQuarter' as FlattenedKeys),
      getRange: () => {
        const lastQuarter = subQuarters(today, 1);
        return {
          startDate: startOfQuarter(lastQuarter),
          endDate: endOfQuarter(lastQuarter)
        };
      }
    },
    {
      type: 'currentYear',
      label: t('period.presets.currentYear' as FlattenedKeys),
      getRange: () => ({
        startDate: startOfYear(today),
        endDate: endOfYear(today)
      })
    },
    {
      type: 'previousYear',
      label: t('period.presets.previousYear' as FlattenedKeys),
      getRange: () => {
        const lastYear = subYears(today, 1);
        return {
          startDate: startOfYear(lastYear),
          endDate: endOfYear(lastYear)
        };
      }
    },
    {
      type: 'last30Days',
      label: t('period.presets.last30Days' as FlattenedKeys),
      getRange: () => ({
        startDate: startOfDay(subDays(today, 29)),
        endDate: endOfDay(today)
      })
    },
    {
      type: 'last90Days',
      label: t('period.presets.last90Days' as FlattenedKeys),
      getRange: () => ({
        startDate: startOfDay(subDays(today, 89)),
        endDate: endOfDay(today)
      })
    },
    {
      type: 'yearToDate',
      label: t('period.presets.yearToDate' as FlattenedKeys),
      getRange: () => ({
        startDate: startOfYear(today),
        endDate: endOfDay(today)
      })
    }
  ];
  
  const handlePresetClick = (preset: Preset) => {
    const range = preset.getRange();
    setSelectedRange(range);
    setActivePreset(preset.type);
    setLeftMonth(startOfMonth(range.startDate));
    setRightMonth(startOfMonth(addMonths(range.startDate, 1)));
  };
  
  const handleDateClick = (date: Date) => {
    if (isSelectingFirstDate) {
      setSelectedRange({
        startDate: startOfDay(date),
        endDate: endOfDay(date)
      });
      setIsSelectingFirstDate(false);
      setActivePreset('custom');
    } else {
      // Ensure start date is before end date
      if (isBefore(date, selectedRange.startDate)) {
        setSelectedRange({
          startDate: startOfDay(date),
          endDate: endOfDay(selectedRange.startDate)
        });
      } else {
        setSelectedRange({
          startDate: selectedRange.startDate,
          endDate: endOfDay(date)
        });
      }
      setIsSelectingFirstDate(true);
    }
  };
  
  const handleDateHover = () => {
    if (!isSelectingFirstDate) {
      // No hover effect needed
    }
  };
  
  // Improved isInRange function to properly check if a date is within the selected range
  const isInRange = (date: Date, currentMonth: Date) => {
    // Only check for range if we have both start and end dates
    if (!selectedRange.startDate || !selectedRange.endDate) return false;
    
    // Normalize all dates to start of day for proper comparison
    const normalizedDate = startOfDay(date);
    const normalizedStart = startOfDay(selectedRange.startDate);
    const normalizedEnd = startOfDay(selectedRange.endDate);
    
    // Check if the date is within the range
    const isWithinRange = (
      (isEqual(normalizedDate, normalizedStart) || isAfter(normalizedDate, normalizedStart)) && 
      (isEqual(normalizedDate, normalizedEnd) || isBefore(normalizedDate, normalizedEnd))
    );
    
    // Only consider it in range if it's in the current month view
    // This prevents dates from being highlighted in the wrong month
    return isWithinRange && isSameMonth(date, currentMonth);
  };
  
  const isRangeStart = (date: Date, currentMonth: Date) => {
    return selectedRange.startDate && 
           isSameDay(date, selectedRange.startDate) && 
           isSameMonth(date, currentMonth);
  };
  
  const isRangeEnd = (date: Date, currentMonth: Date) => {
    return selectedRange.endDate && 
           isSameDay(date, selectedRange.endDate) && 
           isSameMonth(date, currentMonth);
  };
  
  const nextMonth = () => {
    setLeftMonth(addMonths(leftMonth, 1));
    setRightMonth(addMonths(rightMonth, 1));
  };
  
  const prevMonth = () => {
    setLeftMonth(subMonths(leftMonth, 1));
    setRightMonth(subMonths(rightMonth, 1));
  };
  
  const renderCalendar = (month: Date) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const startDate = startOfWeek(monthStart, { locale });
    const endDate = endOfWeek(monthEnd, { locale });
    
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Get day names based on locale
    const weekDays = Array.from({ length: 7 }).map((_, i) => {
      const date = addDays(startOfWeek(new Date(), { locale }), i);
      return format(date, 'EEEEEE', { locale }); // Short weekday name
    });
    
    return (
      <div className="flex flex-col">
        <div className="mb-3 text-center">
          <h3 className="text-sm font-medium text-gray-900">
            {format(month, 'MMMM yyyy', { locale })}
          </h3>
        </div>
        
        <div className="grid grid-cols-7 mb-2">
          {weekDays.map((day, i) => (
            <div 
              key={i} 
              className="h-8 flex items-center justify-center text-xs text-gray-500 font-medium"
            >
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, i) => {
            const isCurrentMonth = isSameMonth(day, month);
            const isStart = isRangeStart(day, month);
            const isEnd = isRangeEnd(day, month);
            const isSelected = isStart || isEnd;
            const isRangeDay = isInRange(day, month);
            const isDayToday = isToday(day);
            
            // Determine if this day is in the middle of a range
            const isMiddleDay = isRangeDay && !isSelected;
            
            // Determine if this is the first day of a range in this row
            const isFirstInRow = i % 7 === 0 || !isInRange(days[i - 1], month);
            
            // Determine if this is the last day of a range in this row
            const isLastInRow = i % 7 === 6 || !isInRange(days[i + 1], month);
            
            return (
              <button
                key={i}
                type="button"
                onClick={() => isCurrentMonth && handleDateClick(day)}
                onMouseEnter={() => isCurrentMonth && handleDateHover()}
                disabled={!isCurrentMonth}
                className={`
                  relative h-9 w-9 flex items-center justify-center text-xs
                  transition-all duration-150 ease-in-out
                  ${!isCurrentMonth ? 'text-gray-300 cursor-default' : 'hover:bg-gray-100'}
                  ${isDayToday && !isSelected && !isRangeDay ? 'text-primary-600 font-medium' : ''}
                  ${isSelected ? 'text-white z-20' : ''}
                  ${isMiddleDay ? 'text-primary-700 z-10' : ''}
                `}
              >
                {/* Background for selected days - only show in the correct month */}
                {isSelected && (
                  <span 
                    className={`absolute inset-0 bg-primary-600 shadow-md
                      ${isStart && !isEnd && !isLastInRow ? 'rounded-l-full rounded-r-none' : ''}
                      ${isEnd && !isStart && !isFirstInRow ? 'rounded-r-full rounded-l-none' : ''}
                      ${(isStart && isEnd) || (isStart && isLastInRow) || (isEnd && isFirstInRow) ? 'rounded-full' : ''}
                      ${!isStart && !isEnd ? 'rounded-none' : ''}
                    `}
                  />
                )}
                
                {/* Background for days in range - with improved visibility */}
                {isMiddleDay && (
                  <span 
                    className={`absolute inset-0 bg-primary-100
                      ${isFirstInRow ? 'rounded-l-full' : ''}
                      ${isLastInRow ? 'rounded-r-full' : ''}
                    `}
                  />
                )}
                
                {/* Day number */}
                <span className={`relative z-30 ${isSelected ? 'font-medium' : ''}`}>
                  {format(day, 'd')}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  
  const handleApply = () => {
    onApply(selectedRange);
    onClose();
  };
  
  const formatDateRange = () => {
    const { startDate, endDate } = selectedRange;
    const dateFormat = 'd MMM yyyy';
    
    return `${format(startDate, dateFormat, { locale })} - ${format(endDate, dateFormat, { locale })}`;
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: position === 'top' ? -10 : 10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: position === 'top' ? -10 : 10, scale: 0.98 }}
          transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          className={`absolute ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} right-0 z-50`}
        >
          <div 
            ref={containerRef}
            className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-[640px] max-w-[calc(100vw-2rem)]"
            style={{ 
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
            }}
          >
            <div className="flex flex-col sm:flex-row">
              {/* Presets sidebar */}
              <div className="w-full sm:w-48 border-b sm:border-b-0 sm:border-r border-gray-100 bg-gray-50">
                <div className="p-4">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                    {t('period.presets.title')}
                  </h3>
                  <div className="space-y-1.5">
                    {presets.map((preset) => (
                      <button
                        key={preset.type}
                        onClick={() => handlePresetClick(preset)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors
                          ${activePreset === preset.type 
                            ? 'bg-primary-50 text-primary-700 font-medium' 
                            : 'text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Calendar section */}
              <div className="flex-1 p-5">
                <div className="flex justify-between items-center mb-5">
                  <div className="flex space-x-1">
                    <button
                      onClick={prevMonth}
                      className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={nextMonth}
                      className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="text-sm text-gray-700 font-medium">
                    {formatDateRange()}
                  </div>
                  
                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {renderCalendar(leftMonth)}
                  {renderCalendar(rightMonth)}
                </div>
                
                <div className="mt-5 flex justify-end">
                  <button
                    onClick={handleApply}
                    className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm hover:shadow"
                  >
                    {t('period.apply')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 